import { Button } from "@material-ui/core";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElementWithChildren } from "backend/model/model";
import React from "react";
import SummaryPopoover from "../popups/SummaryPopover";
import clsx from "clsx";
import ExpandMore from "@material-ui/icons/ExpandMore";

const useStrategyCardStyles = makeThemedStyles("StrategyCard", (theme) => ({
  root: {
    width: "300px",
    borderRadius: "2px",
    color: "white",
  },
  card: {
    width: "100%",
    height: "130px",
    borderRadius: "1.6em",
    overflow: "hidden",
    position: "relative",
    background:
      "linear-gradient(270deg, rgba(16, 41, 117, 0.37) -0.1%, rgba(16, 41, 117, 0.83) 83.43%)",
  },
  content: {
    width: "100%",
    padding: "1.9em 2em 1.4em 2em",
    height: "calc(100% - 3.8em)",
    cursor: "pointer",
    background:
      "linear-gradient(270deg, rgba(16, 41, 117, 0.17) -0.1%, rgba(16, 41, 117, 0.83) 83.43%), var(--backgroundImage)",
  },
  type: {
    color: "rgba(245, 246, 246, 1)",
    fontSize: "1.2em",
    lineHeight: "1.25",
    fontWeight: 500,
    marginBottom: "0.66em",
    display: "none",
  },
  name: {
    color: "rgba(255, 255, 255, 1)",
    fontSize: "1.4em",
    lineHeight: "1.428",
    fontWeight: 600,
    letterSpacing: "0.05625px",
    maxWidth: "15.07em",
  },
  title: {
    color: "white",
  },
  titleTypeName: {
    color: "white",
  },
  cardActions: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "3.8em",
  },
  cardActionsButton: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "100%",
    color: "rgba(16, 41, 117, 1)",
    fontSize: "1.2em",
    borderRadius: 0,
    display: "flex",
    justifyContent: "space-between",
    padding: "0 1.5833em",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  range: {
    position: "absolute",
    top: "1.2em",
    right: "1.2em",
    height: "1.8em",
    width: "1.8em",
    borderRadius: "50%",
    background: "rgba(221, 81, 70, 1)",
    border: "2px solid rgb(255, 255, 255)",
  },
}));

export const StrategyCardTitle = (props: React.PropsWithChildren<{}>) => {
  const styles = useStrategyCardStyles();
  return <div className={styles.title}>{props.children}</div>;
};

export const StrategyCardTypeName = (props: React.PropsWithChildren<{}>) => {
  const styles = useStrategyCardStyles();
  return <div className={styles.titleTypeName}>{props.children}</div>;
};

export const StrategyCard = (
  props: React.PropsWithChildren<{
    element: ICardElementWithChildren;
    backgroundImage?: string;
    primaryColor?: string;
    textColor?: string;
    rtl?: boolean;
    width?: string|undefined;
    onSelect?: () => void;
    typenames: {
      name: string;
      title: string;
    }[];
  }>
) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const [showPopover, setShowPopover] = React.useState(false);
  const styles = useStrategyCardStyles();
  const { element: el } = props;
  const childrenFlat = React.useMemo(
    () => flattenHierarchy(props.element.children),
    [props.element]
  );
  const primaryColor = props.primaryColor
    ? props.primaryColor
    : "rgba(16, 41, 117, 0.8)";

  const typeCounts = React.useMemo(() => {
    return props.typenames.map((tn) => {
      return {
        typename: tn.name,
        title: tn.title,
        count: childrenFlat.filter((el) => el.typename === tn.name).length,
      };
    });
  }, [childrenFlat]);
  const totalChildCount = React.useMemo(() => {
    return typeCounts.reduce((prev, curr) => prev + curr.count, 0);
  }, [typeCounts]);
  return (
    <div
      className={styles.root}
      role="button"
      style={{
        cursor: props.onSelect ? "pointer" : undefined,
        width: props.width,
        fontSize: props.width ? (parseInt(props.width, 10) < 50 ? "0.65em" :   parseInt(props.width, 10) < 70 ? "0.75em" :  parseInt(props.width, 10) < 90 ? "0.9em" : undefined): undefined
      }}
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={() => {
        if (props.onSelect && !showPopover) {
          props.onSelect();
        }
      }}
    >
      <div className={styles.card}>
        <div
          className={styles.range}
          title={el.rangename || "No value"}
          style={{ background: `#${el.rangecolor || "000000"}aa` }}
        ></div>
        <div
          className={styles.content}
          style={{
            background: `linear-gradient(270deg, ${primaryColor} -0.1%, ${primaryColor} 83.43%), url(${props.backgroundImage})`,
          }}
        >
          <div className={styles.type} style={{ color: props.textColor }}>
            {el.typename}{" "}
          </div>
          <div className={styles.name} style={{ color: props.textColor }}>
            {el.name}
          </div>
        </div>

        <div className={styles.cardActions}>
          <Button
            onClick={(e) => {
              setShowPopover(true);
              e.preventDefault();
              e.stopPropagation();
            }}
            className={clsx(styles.cardActionsButton, props.rtl && "RTL")}
            endIcon={totalChildCount > 0 ? <ExpandMore /> : null}
            ref={ref}
          >
            {totalChildCount > 0
              ? typeCounts.map((t, index) => {
                  if (t.count === 0) {
                    return null;
                  }
                  return (
                    <React.Fragment key={t.title}>
                      {index > 0 ? " and " : ""}
                      {t.count} {t.title}
                    </React.Fragment>
                  );
                })
              : ""}
          </Button>

          <SummaryPopoover
            onClose={() => {
              setShowPopover(false);
            }}
            items={[el]}
            anchor={ref.current}
            typenames={["KPI", "Initiative"]}
            isOpen={showPopover}
          />
        </div>
      </div>
    </div>
  );
};
/*
    <div className="Card">
      <div className={clsx('Range', rangeClass, ctx.rtl && 'RTL')} />
      <div className="CardContent" onMouseDown={redirectTo} onClick={redirectTo}>
        <Typography className="Type">Strategic Objective</Typography>
        <Typography className="Name">{getElementName(item, ctx.language)}</Typography>
      </div>
      <div className="CardActions">
        <Button
          onClick={() => setSubMenu(true)}
          className={clsx('Button', ctx.rtl && 'RTL')}
          endIcon={<ExpandMoreIcon />}
          ref={buttonRef}
        >
          {counts.kpis} KPIs and {counts.inititives} Inititives
        </Button>
        <Popover
          open={subMenu}
          anchorEl={buttonRef.current}
          onClose={() => setSubMenu(false)}
          classes={{ paper: classes.paper }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: ctx.rtl ? 'left' : 'right'
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
          dir={ctx.rtl ? 'rtl' : 'ltr'}
        >
          <SubMenus ranges={itemRanges} />
        </Popover>
      </div>
    </div>
*/
/*
                '& .Card': {
                  marginLeft: '3em',
                  height: '13.4em',
                  width: '24.9em',
                  borderRadius: '1.6em',
                  overflow: 'hidden',
                  position: 'relative',
                  '& .Range': {
                    position: 'absolute',
                    top: '1.2em',
                    right: '1.2em',
                    height: '1.8em',
                    width: '1.8em',
                    borderRadius: '50%',
                    background: 'rgba(221, 81, 70, 1)',
                    border: '2px solid rgb(255, 255, 255)',
                    '&.RTL': {
                      left: '1.2em',
                      right: 'unset'
                    },
                    '&.Good': {
                      background: 'rgba(117, 187, 121, 1)'
                    },
                    '&.Average': {
                      background: 'rgba(247, 207, 82, 1)'
                    }
                  },
                  '& .CardContent ': {
                    width: '100%',
                    padding: '1.9em 2em 1.4em 2em',
                    height: 'calc(100% - 3.8em)',
                    cursor: 'pointer',
                    background:
                      'linear-gradient(270deg, rgba(16, 41, 117, 0.17) -0.1%, rgba(16, 41, 117, 0.83) 83.43%), var(--backgroundImage)',
                    '& .Type': {
                      color: 'rgba(245, 246, 246, 1)',
                      fontSize: '1.2em',
                      lineHeight: '1.25',
                      fontWeight: 500,
                      marginBottom: '0.66em'
                    },
                    '& .Name': {
                      color: 'rgba(255, 255, 255, 1)',
                      fontSize: '1.4em',
                      lineHeight: '1.428',
                      fontWeight: 600,
                      letterSpacing: '0.05625px',
                      maxWidth: '15.07em'
                    }
                  },
                  '& .CardActions ': {
                    background: 'rgba(255, 255, 255, 1)',
                    width: '100%',
                    height: '3.8em',
                    '& .Button': {
                      background: 'rgba(255, 255, 255, 1)',
                      width: '100%',
                      height: '100%',
                      color: 'rgba(16, 41, 117, 1)',
                      fontSize: '1.2em',
                      borderRadius: 0,
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0 1.5833em',
                      '&.RTL': {
                        '& svg': {
                          transform: 'rotate(90deg)'
                        }
                      },
                      '& svg': {
                        fill: 'rgba(63, 69, 79, 1)',
                        transform: 'rotate(-90deg)'
                      }
                    }
                  }
                }
              }

*/
